import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Backup {
  id: string
  name: string
  type: string
  status: string
  size_gb: number
  duration_min: number
  created_at: string
  retention_days: number
}

interface Schedule {
  id: string
  name: string
  type: string
  frequency: string
  time: string
  enabled: boolean
  last_run: string | null
  next_run: string
}

export default function BackupDashboardPage() {
  const [backups, setBackups] = useState<Backup[]>([])
  const [schedules, setSchedules] = useState<Schedule[]>([])
  const [recoveryPoints, setRecoveryPoints] = useState<any[]>([])
  const [statistics, setStatistics] = useState<any>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [backupRes, schedRes, rpRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/backup/backups'),
      fetch(API_BASE + '/backup/schedules'),
      fetch(API_BASE + '/backup/recovery-points'),
      fetch(API_BASE + '/backup/statistics')
    ])
    if (backupRes.ok) setBackups((await backupRes.json()).backups || [])
    if (schedRes.ok) setSchedules((await schedRes.json()).schedules || [])
    if (rpRes.ok) setRecoveryPoints((await rpRes.json()).recovery_points || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const createBackup = async (type: string) => {
    await fetch(API_BASE + '/backup/backups', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: `Manual ${type} Backup`, type })
    })
    fetchData()
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800'
      case 'in_progress': return 'bg-blue-100 text-blue-800'
      case 'failed': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Backup & Disaster Recovery</h1>
          <div className="flex gap-2">
            <button onClick={() => createBackup('incremental')} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Incremental Backup</button>
            <button onClick={() => createBackup('full')} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">Full Backup</button>
          </div>
        </div>

        {statistics && (
          <div className="grid grid-cols-6 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Backups</div>
              <div className="text-2xl font-bold">{statistics.total_backups}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Completed</div>
              <div className="text-2xl font-bold text-green-600">{statistics.completed_backups}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Storage Used</div>
              <div className="text-2xl font-bold">{statistics.total_storage_gb?.toFixed(1)} GB</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Recovery Points</div>
              <div className="text-2xl font-bold">{statistics.recovery_points}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Success Rate</div>
              <div className="text-2xl font-bold text-green-600">{statistics.backup_success_rate}%</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Last Backup</div>
              <div className="text-sm font-medium">{statistics.last_backup ? new Date(statistics.last_backup).toLocaleString() : 'N/A'}</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Recent Backups</h2></div>
            <div className="divide-y">
              {backups.map(backup => (
                <div key={backup.id} className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <div className="font-semibold">{backup.name}</div>
                      <div className="text-sm text-gray-500">{new Date(backup.created_at).toLocaleString()}</div>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(backup.status)}`}>{backup.status}</span>
                  </div>
                  <div className="flex gap-4 text-sm text-gray-500">
                    <span className="px-2 py-1 bg-gray-100 rounded">{backup.type}</span>
                    <span>{backup.size_gb} GB</span>
                    {backup.duration_min > 0 && <span>{backup.duration_min} min</span>}
                    <span>{backup.retention_days} days retention</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Backup Schedules</h2></div>
            <div className="divide-y">
              {schedules.map(schedule => (
                <div key={schedule.id} className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <div className="font-semibold">{schedule.name}</div>
                      <div className="text-sm text-gray-500">{schedule.frequency} at {schedule.time}</div>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs ${schedule.enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                      {schedule.enabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                  <div className="flex gap-4 text-sm text-gray-500">
                    <span>Type: {schedule.type}</span>
                    <span>Next: {new Date(schedule.next_run).toLocaleString()}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Recovery Points</h2></div>
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Backup</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Timestamp</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Verified</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {recoveryPoints.map(rp => (
                <tr key={rp.id}>
                  <td className="px-6 py-4 font-mono text-sm">{rp.id}</td>
                  <td className="px-6 py-4 text-sm">{rp.backup_id}</td>
                  <td className="px-6 py-4"><span className="px-2 py-1 bg-gray-100 rounded text-xs">{rp.type}</span></td>
                  <td className="px-6 py-4 text-sm">{new Date(rp.timestamp).toLocaleString()}</td>
                  <td className="px-6 py-4">{rp.verified ? <span className="text-green-600">Yes</span> : <span className="text-gray-400">No</span>}</td>
                  <td className="px-6 py-4">
                    <button className="px-3 py-1 text-xs bg-blue-100 text-blue-800 rounded hover:bg-blue-200">Restore</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  )
}
